import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Route, Switch, useLocation } from 'react-router-dom';
import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import AccountShell from '../../../layouts/account/AccountShell';
import TesterCycleNav from '../../../_components/accounts/tester/TesterCycleNav';
import cycleActions from '../../../redux/actions/cycleActions';
import { RootState } from '../../../redux/store';
import AccountTester from '../../../components/Account/Tester/AccountTester';
import AccountTesterProfile from '../../../components/Account/Tester/MyProfile/MyProfile';
import AccountTesterReviews from '../../../components/Account/Tester/CustomerReviews';
import Affiliates from '../../../components/Account/Tester/Affiliates';
import AccountTesterCycleDetails from '../../../components/Account/Tester/Cycle/Details';
import AccountTesterCycleApply from '../../../components/Account/Tester/Cycle/Apply';
import AccountTesterCycleTestPlan from '../../../components/Account/Tester/Cycle/TesterTestPlan';
import AccountTesterCycleNewBug from '../../../components/Account/Tester/Cycle/NewBug';
import AccountTesterCycleBugs from '../../../components/Account/Tester/Cycle/Bugs';
import AccountTesterCycleEditBug from '../../../components/Account/Tester/Cycle/EditBug';
import AccountTesterCycleMessages from '../../../components/Account/Tester/Cycle/Messages';

/* Every /account/tester/* page inside one AccountShell. Cycle pages get TesterCycleNav (header +
   tabs) above their content. Login/status checks happen in the PrivateRoute that renders this. */
const TesterArea: React.FC = () => {
    const dispatch = useDispatch();
    const { cycles } = useSelector((state: RootState) => state);
    const { pathname } = useLocation();
    const [cyclesLoaded, setCyclesLoaded] = useState(cycles.length > 0);

    // Cycle pages read their cycle (and its bugs) from this list when they are created, so they
    // must not render before it arrives: on a direct URL load they used to show blank or crash.
    // Refreshed every 30 s, as the old tester sidebar did (new invitations, status changes).
    useEffect(() => {
        if (!cycles.length) {
            Promise.resolve(dispatch(cycleActions.listCycles())).finally(() => setCyclesLoaded(true));
        }
        const interval = setInterval(() => dispatch(cycleActions.listCycles()), 30000);
        return () => clearInterval(interval);
    }, []);

    if (!cyclesLoaded && pathname.startsWith('/account/tester/cycle/')) {
        return (
            <AccountShell>
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 10 }}>
                    <CircularProgress />
                </Box>
            </AccountShell>
        );
    }

    return (
        <AccountShell>
            <Route
                path="/account/tester/cycle/:id"
                render={({ match }) => <TesterCycleNav cycleId={+match.params.id} />}
            />
            <Switch>
                <Route exact path="/account/tester" component={AccountTester} />
                <Route path="/account/tester/myprofile/:tab?" component={AccountTesterProfile} />
                <Route path="/account/tester/reviews" component={AccountTesterReviews} />
                <Route path="/account/tester/affiliates" component={Affiliates} />
                <Route exact path="/account/tester/cycle/:id" component={AccountTesterCycleDetails} />
                <Route path="/account/tester/cycle/:id/apply" component={AccountTesterCycleApply} />
                <Route path="/account/tester/cycle/:id/testplan" component={AccountTesterCycleTestPlan} />
                <Route path="/account/tester/cycle/:id/newbug" component={AccountTesterCycleNewBug} />
                <Route exact path="/account/tester/cycle/:id/bugs" component={AccountTesterCycleBugs} />
                <Route path="/account/tester/cycle/:cycleId/bugs/:bugId/edit" component={AccountTesterCycleEditBug} />
                <Route path="/account/tester/cycle/:id/messages" component={AccountTesterCycleMessages} />
            </Switch>
        </AccountShell>
    );
};

export default TesterArea;
